import { useState } from 'react';

type SuggestionItem = {
  competencyId: number;
  competencyStage?: 'I' | 'II' | 'III';
  name: string;
  confidence: number;
  reason: string;
  suggestedWeightPercent: number;
};

type Props = {
  items: SuggestionItem[];
  onConfirm: (payload: any) => void;
  loading?: boolean;
};

export default function ClassworkCompetencySelector({ items, onConfirm, loading }: Props) {
  const itemKey = (item: SuggestionItem) => `${item.competencyId}-${item.competencyStage || 'I'}`;
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const activeItems = items.filter((item) => selected[itemKey(item)]);
  const totalSuggestedWeight = activeItems.reduce((sum, item) => sum + (Number(item.suggestedWeightPercent) || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Kompetenzvorschläge</h3>
      {!items.length && <p className="text-sm text-gray-500">Noch keine Vorschläge vorhanden.</p>}
      {!!items.length && (
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-600">
            Ausgewählt: {activeItems.length} / {items.length} · vorgeschlagenes Gewicht: {totalSuggestedWeight.toFixed(0)}%
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() =>
                setSelected(Object.fromEntries(items.map((item) => [itemKey(item), true])) as Record<string, boolean>)
              }
            >
              Alle wählen
            </button>
            <button type="button" className="btn-secondary text-xs" onClick={() => setSelected({})}>
              Auswahl leeren
            </button>
          </div>
        </div>
      )}
      <div className="space-y-2 max-h-64 overflow-auto pr-1">
        {items.map((item) => (
          <label
            key={itemKey(item)}
            className="flex items-start gap-2 p-3 border-2 border-gray-200 rounded-lg hover:border-primary-300 hover:bg-gray-50 transition-all"
          >
            <input
              type="checkbox"
              checked={!!selected[itemKey(item)]}
              onChange={(e) => setSelected((prev) => ({ ...prev, [itemKey(item)]: e.target.checked }))}
            />
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                <span>{item.name}</span>
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                  {item.competencyStage || 'I'}
                </span>
              </div>
              <div className="text-xs text-gray-500">Confidence: {item.confidence.toFixed(1)}%</div>
              <div className="text-xs text-gray-500">{item.reason}</div>
            </div>
          </label>
        ))}
      </div>
      <button
        className="btn-secondary mt-3"
        disabled={loading || !activeItems.length}
        onClick={() =>
          onConfirm({
            mode: 'replace',
            competencies: activeItems.map((item, index) => ({
              competencyId: item.competencyId,
              competencyStage: item.competencyStage || 'I',
              weightPercent: item.suggestedWeightPercent || 0,
              priorityRank: index + 1,
              llmSuggested: true,
              llmConfidence: item.confidence,
              suggestionReason: item.reason,
              evidenceThresholdPercent: 60
            }))
          })
        }
      >
        Auswahl bestätigen
      </button>
    </div>
  );
}
